import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import { X, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import PricingDialog from "@/components/PricingDialog";
import { trackConversion } from "@/lib/trackConversion";

const SESSION_KEY = "exit_intent_shown";
const EXIT_DISMISSED_KEY = "exit_intent_dismissed";
const FLASH_SESSION_KEY = "flash_offer_shown";

const ExitIntentModal = () => {
  const { isPremium } = useAuth();
  const { formatPriceWithEur } = useLocalCurrency();
  const [show, setShow] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [countdown, setCountdown] = useState(600); // 10 minutes
  const shownRef = useRef(sessionStorage.getItem(SESSION_KEY) === "1");
  const inactivityTimer = useRef<ReturnType<typeof setTimeout>>();

  const dismissModal = useCallback(() => {
    setShow(false);
    sessionStorage.setItem(EXIT_DISMISSED_KEY, "1");
    window.dispatchEvent(new Event("exit_intent_dismissed"));
  }, []);

  const triggerModal = useCallback(() => {
    // Don't show if flash offer already shown (limit to 1 popup per session)
    if (shownRef.current || isPremium || sessionStorage.getItem(FLASH_SESSION_KEY) === "1") return;
    shownRef.current = true;
    sessionStorage.setItem(SESSION_KEY, "1");
    setShow(true);
    trackConversion("exit_intent_shown");
  }, [isPremium]);

  // Desktop: mouse leaves viewport top
  useEffect(() => {
    if (isPremium || shownRef.current) return;
    const handler = (e: MouseEvent) => {
      if (e.clientY <= 5) triggerModal();
    };
    document.addEventListener("mouseout", handler);
    return () => document.removeEventListener("mouseout", handler);
  }, [isPremium, triggerModal]);

  // Mobile: 60s inactivity
  useEffect(() => {
    if (isPremium || shownRef.current) return;
    const resetTimer = () => {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(triggerModal, 60000);
    };
    resetTimer();
    window.addEventListener("touchstart", resetTimer);
    window.addEventListener("scroll", resetTimer);
    return () => {
      clearTimeout(inactivityTimer.current);
      window.removeEventListener("touchstart", resetTimer);
      window.removeEventListener("scroll", resetTimer);
    };
  }, [isPremium, triggerModal]);

  // Countdown timer
  useEffect(() => {
    if (!show) return;
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          setShow(false);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [show]);

  if (isPremium) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
            onClick={dismissModal}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: "spring", stiffness: 250, damping: 22 }}
              className="relative max-w-md w-full rounded-2xl border border-primary/30 bg-card p-6 shadow-2xl shadow-primary/10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={dismissModal}
                className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15">
                  <Zap className="h-7 w-7 text-primary" />
                </div>

                <h2 className="text-xl font-black text-foreground">
                  Wait — don't leave unprotected
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Conflicts are evolving right now. Get full access to real-time intelligence, AI analysis, and early warnings.
                </p>

                <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-destructive/10 px-4 py-2">
                  <Clock className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-bold text-destructive">
                    Offer expires in {minutes}:{seconds.toString().padStart(2, "0")}
                  </span>
                </div>

                <Button
                  size="lg"
                  className="mt-4 w-full gap-2 font-bold text-base"
                  onClick={() => {
                    trackConversion("exit_intent_clicked");
                    setShow(false);
                    setPricingOpen(true);
                  }}
                >
                  <Zap className="h-4 w-4" />
                  Claim your access — {formatPriceWithEur(2.99)}/mo
                </Button>
                <p className="mt-1.5 text-[10px] text-muted-foreground">
                  Cancel anytime • Secure payment via Stripe
                </p>

                <button
                  onClick={dismissModal}
                  className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  No thanks, I'll stay uninformed
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </>
  );
};

export default ExitIntentModal;
